import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { PExpandAreaTriggerComponent } from '@panaya/ui/p-expand-area-trigger';
import { PFieldBase } from '@panaya/ui/p-field';
import { ValueAccessorDirective } from '@panaya/ui/p-value-accessor';
import { HtmlInputBoolean, HtmlInputNumber } from '@panaya/ui/pipes';
import { getAutomationId } from '@panaya/utils';
import { MentionConfig } from 'angular-mentions';
import { Subscription } from 'rxjs';
import { TextAreaEnum } from './text-area-enum';
import { IconName } from '@panaya/ui/p-icon';
import { ColorName } from '@panaya/ui/styles-to-code';

@Component({
    selector: 'p-field-text',
    templateUrl: './p-field-text.component.html',
    styleUrls: ['./p-field-text.component.scss'],
    hostDirectives: [ValueAccessorDirective],
    providers: [
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: PFieldTextComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldText'
})
export class PFieldTextComponent extends PFieldBase<string> implements AfterViewInit, OnChanges, Validator, DoCheck, OnDestroy {
    /* start - inputs has taken from pFieldAbstract - to move to a generic place... */

    /**
     * This input will reflect the native HTML `required` attribute.
     * Angular Forms will manage this validation for us, hence it won't appear in our `p-field-text` template.
     */
    @Input('required') public required: HtmlInputBoolean = false;
    /**
     * This input will reflect the native HTML `maxlength` attribute.
     * TODO: Make sure we tell Angular Forms about the default value of 255 when no maxlength has been passed-in
     *       and we are in single-line mode (see <input [attr.maxlength]... in our template)
     */
    @Input('maxlength') public maxLength?: HtmlInputNumber;

    @Input() public valueTooltip = '';
    @Input() public labelTooltip = '';
    @Input() public shouldTransformLabelTextCase = true;
    @Input() public isReadOnly = false;
    @Input() public isDisabled = false;

    /* end */

    @Input() public label?: string;
    @Input() public ariaLabel?: string;
    @Input() public iconName?: IconName;
    @Input() public iconColor: ColorName = 'base-dark';
    @Input() public type: 'password' | 'text' = 'text';
    @Input() public errorMessage?: string;
    @Input() public matcher: ErrorStateMatcher = {
        // Implemented the default behavior described in Angular docs:
        // https://material.angular.io/components/input/overview#changing-when-error-messages-are-shown
        isErrorState: (control, form) => !!control && control.invalid && (control.touched || (!!form && form.submitted))
    };

    @Input() public isMultiline = false;
    @Input() public isExpandable = false;
    @Input() public customClasses?: string;

    //@Input() public maxLength?: number;
    @Input() public placeholder = '';
    @Input() public placeholderText = '';
    @Input() public autoFocus = false;
    @Input() public autoFocusAfterMs = 0;

    @Input() public mentionCfg: MentionConfig = {} as MentionConfig;
    @Input() public hasInputListener = false;
    @Input() public trimText = false;
    @Input() public shouldUpdateCursorPosition = false;

    // todo: rethinking about AI code design is recommended once the requirements will be more stable
    @Input() public isAiEnabled = false;
    @Input() public aiTextKey: string = '';
    @Input() public aiTooltipKey: string = '';
    @Output() public readonly aiClicked = new EventEmitter<void>();
    protected isAIInProgress: boolean = false;

    @Output() public enterPressed = new EventEmitter<KeyboardEvent>();
    @Output() public keyup = new EventEmitter<KeyboardEvent>();
    @Output() public mentionAdded = new EventEmitter<any>();
    @Output() public heightChanged = new EventEmitter<number>();
    @Output() public cursorPositionChanged = new EventEmitter<number>();
    @Output() public actionIconClicked = new EventEmitter<void>();

    @Input() public dynamicStyle: { [klass: string]: any } | null | undefined = undefined;

    @Input() public size = TextAreaEnum.ExtendedHeight;
    @Input() public autoSizeMinRows = 4;
    @Input() public autoSizeMaxRows = 8;
    @Input() public actionIconName?: IconName;
    @Input() public actionIconTooltip?: string;
    @Input() public actionIconUrl?: string;
    private realAutoSizeMaxRows = this.autoSizeMaxRows;

    public textAreaEnum = TextAreaEnum;
    private prevHeight?: number | null = null;
    private valueChangedSubscription: Subscription;
    @ViewChild('pInput') private readonly pInput?: ElementRef<HTMLInputElement>;
    @ViewChild('pTextarea') private readonly pTextarea?: ElementRef<HTMLInputElement>;
    @ViewChild('autosize') private readonly autoSize!: CdkTextareaAutosize;
    @ViewChild('expandAreaTrigger') private readonly expandAreaTrigger?: PExpandAreaTriggerComponent;

    protected readonly ngZone = inject(NgZone);

    constructor() {
        super();
        this.valueAccessor.disabledStateChanged.subscribe((isDisabled) => this.onDisabledChanged(isDisabled));
        this.valueChangedSubscription = this.valueAccessor.valueChanged.subscribe(() => {
            if (this.isAiEnabled && this.isAIInProgress) {
                this.resizeTextArea();
                this.isAIInProgress = false;
            }
        });
    }

    public ngDoCheck(): void {
        if (this.isExpandable) {
            this.autoSize?.ngDoCheck();
        }
    }

    public ngOnDestroy(): void {
        this.valueChangedSubscription?.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['autoSizeMaxRows']?.currentValue) {
            this.realAutoSizeMaxRows = changes['autoSizeMaxRows'].currentValue;
        }
    }

    public ngAfterViewInit(): void {
        if (this.autoFocus) {
            this.focus();
        }

        if (this.size == TextAreaEnum.LargeResize) {
            setTimeout(() => {
                this.prevHeight = this.pTextarea?.nativeElement?.offsetHeight;
                this.autoSize.resizeToFitContent(true);
            }, 0);
        }

        if (this.isMultiline && this.isExpandable) {
            this.autoSize.resizeToFitContent(true);
        }
    }

    public changeValue(event: Event): void {
        if (!event.target) return;

        const target = event.target as HTMLInputElement | HTMLTextAreaElement;

        if ((event as InputEvent).inputType !== 'deleteContentForward') {
            target.value = target.value.trimStart();
        }
        if (this.trimText) target.value = target.value.trim();
        this.valueAccessor.markAsTouched();

        if (!this.isReadOnly) {
            this.setValue(target.value);
        }
        this.resizeTextArea();
    }

    private resizeTextArea() {
        if (this.pTextarea && this.size == TextAreaEnum.LargeResize) {
            const offsetHeight = this.pTextarea.nativeElement.offsetHeight;

            if (offsetHeight && this.prevHeight !== offsetHeight) {
                this.heightChanged.emit(offsetHeight);
                this.prevHeight = offsetHeight;
            }
            if (!this.prevHeight) {
                this.prevHeight = offsetHeight;
            }
        }

        if (this.isMultiline && this.isExpandable) {
            this.expandAreaTrigger?.triggerCheckIsAreaExpandable();
        }
    }

    protected onInput(ev: Event): void {
        if (this.hasInputListener) {
            this.changeValue(ev);
        }
    }

    protected onEnterPressed($event: Event): void {
        this.enterPressed.emit($event as KeyboardEvent);
    }

    protected onTextAreaEscapePressed({ target }: Event): void {
        if (!target) throw new Error(`Failed to escape textarea. Event 'target' is null.`);

        (target as HTMLElement).blur();
    }

    public validate(control: AbstractControl<string>): ValidationErrors | null {
        return control.value && textContainsOnlySpaces(control.value) ? { textContainsOnlySpaces: true } : null;
    }

    public focus(): void {
        setTimeout(() => {
            this.pInput?.nativeElement.focus();
            this.pTextarea?.nativeElement.focus();
        }, this.autoFocusAfterMs);
    }

    public getCustomClasses(): string {
        // ✅ Using component-specific class with new design system typography
        return `p-field-text-paragraph ${this.customClasses}${this.isMultiline ? ' p-field-text-multi-line' : ''}`;
    }

    get automationId(): string {
        return getAutomationId('p-field-text', this.label);
    }

    public get inputTooltip(): string {
        return this.valueAccessor.value ?? '';
    }

    public get shouldShowError(): boolean {
        return !!this.errorMessage;
    }

    public get isAutoResizable(): boolean {
        return this.size === this.textAreaEnum.LargeResize || (this.isMultiline && this.isExpandable);
    }

    onBlurTextArea(ev: any): void {
        if (textContainsOnlySpaces(ev.target.value)) {
            ev.target.value = ev.target.value.trim();
        }
    }

    public getFormFieldClasses(): string {
        return this.isMultiline
            ? 'p-input-outline p-textarea ' + this.size
            : 'p-input-text ' + (this.isReadOnly ? 'p-input-text-readonly' : '');
    }

    protected emitCursorPositionChange(): void {
        const fieldElement = (this.pInput ?? this.pTextarea)?.nativeElement;
        this.cursorPositionChanged.emit(fieldElement?.selectionStart ?? 0);
    }

    public setCursorPosition(position: number): void {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                const element = (this.pInput ?? this.pTextarea)?.nativeElement;
                if (element) {
                    element.setSelectionRange(position, position);
                    this.emitCursorPositionChange();
                }
            });
        });
    }

    protected expandElement(expand: boolean): void {
        this.autoSizeMaxRows = expand ? 10000 : this.realAutoSizeMaxRows;
        this.autoSize.resizeToFitContent(true);
    }

    protected aiTriggered(): void {
        this.aiClicked.emit();
        this.isAIInProgress = true;
    }

    private onDisabledChanged(isDisabled: boolean): void {
        // When programatically disabling the form control, by design we want our field to go into readonly mode.
        // (Also in pFieldAbstract) In Angular v15 setDisabledState is always called when a ControlValueAccessor is attached.
        // In our components, we don't want to match isReadOnly with the disabled state on init.
        //
        // This is a workaround to replicate the new config of Angular's FormsModule, which was not viable
        // as it requires refactoring all FormsModule imports in our code.
        // https://angular.io/guide/update-to-version-15#setdisabledstate-is-always-called-when-a-controlvalueaccessor-is-attached
        if (this.isReadOnly && !isDisabled) {
            return;
        }

        this.isReadOnly = isDisabled;
    }
}

function textContainsOnlySpaces(text: string): boolean {
    return text.length > 0 && text.trim().length === 0;
}
